import { useEffect, useState } from "react";
import { supAPI, fmtINR } from "./supApi";
import { toast } from "sonner";
import { Plus, Trash2, Ban } from "lucide-react";
import { EmptyState } from "./SupplyItems";
import { Modal, Fld, Footer, cls } from "./SupplyStock";
import { DatePicker } from "@/components/ui/date-picker";
import { NumberInput } from "@/components/ui/number-input";

const STATUS_STYLES = {
  draft:               "bg-slate-100 text-slate-500",
  sent:                "bg-sky-50 text-sky-700",
  partially_received:  "bg-amber-50 text-amber-700",
  received:            "bg-emerald-50 text-emerald-700",
  cancelled:           "bg-rose-50 text-rose-700",
};

export default function SupplyPOs() {
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [approvedPRs, setApprovedPRs] = useState([]);
  const [status, setStatus] = useState("");
  const [creating, setCreating] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);

  const load = () => supAPI.pos({ status: status || undefined }).then(setRows).catch(() => {});
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  useEffect(() => {
    supAPI.items().then(setItems);
    supAPI.vendors().then(setVendors);
    supAPI.prs({ status: "approved" }).then(setApprovedPRs);
  }, []);

  return (
    <div className="space-y-4" data-testid="sup-pos">
      <div className="flex items-center gap-3 flex-wrap">
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="sup-po-filter" aria-label="Filter by status"
                className={cls + " max-w-[200px]"}>
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="partially_received">Partially received</option>
          <option value="received">Received</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button onClick={() => setCreating(true)} disabled={!vendors.length || !items.length}
                data-testid="sup-po-new"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-50">
          <Plus className="w-4 h-4"/> New Order
        </button>
      </div>

      {!rows.length ? <EmptyState msg="No purchase orders yet. Create one directly or convert an approved PR."/> : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11.5px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Vendor</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Expected</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(r => (
                <tr key={r.id} data-testid={`sup-po-row-${r.id}`} onClick={() => setSelectedPO(r)}
                    className="hover:bg-slate-50/50 cursor-pointer">
                  <td className="px-4 py-3 mono text-slate-500">{r.code}</td>
                  <td className="px-4 py-3 font-medium text-hocp-navy">{r.vendor_name}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium">{fmtINR(r.total_amount)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${STATUS_STYLES[r.status]}`}>{r.status.replace("_", " ")}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{r.expected_date || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <POCreateDialog items={items} vendors={vendors} approvedPRs={approvedPRs}
                                    onClose={() => setCreating(false)}
                                    onDone={() => { setCreating(false); load(); supAPI.prs({ status: "approved" }).then(setApprovedPRs); }}/>}
      {selectedPO && <PODetailDialog po={selectedPO} items={items}
                                       onClose={() => setSelectedPO(null)}
                                       onDone={() => { setSelectedPO(null); load(); }}/>}
    </div>
  );
}

function POCreateDialog({ items, vendors, approvedPRs, onClose, onDone }) {
  const [prId, setPrId] = useState("");
  const [vendorId, setVendorId] = useState(vendors[0]?.id || "");
  const [expected, setExpected] = useState("");
  const [lines, setLines] = useState([{ item_id: items[0]?.id || "", qty: 1, unit_cost: 0 }]);
  const [saving, setSaving] = useState(false);

  // Autofill lines when a PR is selected
  useEffect(() => {
    if (!prId) return;
    const pr = approvedPRs.find(p => p.id === prId);
    if (pr) {
      setLines(pr.items.map(li => {
        const it = items.find(i => i.id === li.item_id);
        return { item_id: li.item_id, qty: li.qty, unit_cost: it?.standard_cost || 0 };
      }));
      const it = items.find(i => i.id === pr.items[0]?.item_id);
      if (it?.preferred_vendor_id) setVendorId(it.preferred_vendor_id);
    }
  }, [prId, approvedPRs, items]);

  const update = (i, patch) => setLines(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const add = () => setLines(prev => [...prev, { item_id: items[0]?.id || "", qty: 1, unit_cost: 0 }]);
  const rm = (i) => setLines(prev => prev.filter((_, idx) => idx !== i));

  const total = lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_cost || 0), 0);
  const [submitted, setSubmitted] = useState(false);
  const lineErr = (l) => !l.item_id ? "Pick an item" : (!(Number(l.qty) > 0) ? "Qty must be greater than 0" : (Number(l.unit_cost) < 0 ? "Unit cost can't be negative" : ""));

  const submit = async () => {
    setSubmitted(true);
    if (!vendorId) { toast.error("Vendor is required"); return; }
    const valid = lines.filter(l => l.item_id && Number(l.qty) > 0 && Number(l.unit_cost) >= 0);
    if (!valid.length) { toast.error("Add at least one valid line (item + quantity)"); return; }
    setSaving(true);
    try {
      await supAPI.createPO({
        pr_id: prId || undefined,
        vendor_id: vendorId,
        expected_date: expected || undefined,
        items: valid.map(l => ({ item_id: l.item_id, qty: Number(l.qty), unit_cost: Number(l.unit_cost) })),
      });
      toast.success("Purchase order created"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="New Purchase Order" onClose={onClose}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Convert from Approved PR" optional>
            <select value={prId} onChange={e => setPrId(e.target.value)} data-testid="po-pr" className={cls}>
              <option value="">— none —</option>
              {approvedPRs.map(pr => <option key={pr.id} value={pr.id}>{pr.code} · {pr.items.length} lines</option>)}
            </select>
          </Fld>
          <Fld label="Vendor" required error={submitted && !vendorId ? "Vendor is required" : ""}>
            <select value={vendorId} onChange={e => setVendorId(e.target.value)} data-testid="po-vendor" className={`${cls} ${submitted && !vendorId ? "border-rose-400" : ""}`}>
              <option value="">Select a vendor…</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </Fld>
          <Fld label="Expected date" optional>
            <DatePicker value={(expected || '').slice(0,10)} onChange={(_v) => setExpected(_v)}/>
          </Fld>
        </div>
        <div>
          <div className="text-[11.5px] uppercase tracking-wider text-slate-500 font-medium mb-2">Lines</div>
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_100px_120px_auto] gap-2 text-[10.5px] uppercase tracking-wider text-slate-400 font-medium">
              <span>Item <span className="text-rose-500">*</span></span><span>Qty <span className="text-rose-500">*</span></span><span>Unit cost (₹)</span><span></span>
            </div>
            {lines.map((l, i) => {
              const err = submitted ? lineErr(l) : "";
              return (
              <div key={i}>
                <div className="grid grid-cols-[1fr_100px_120px_auto] gap-2 items-center">
                  <select value={l.item_id} onChange={e => update(i, { item_id: e.target.value })}
                          data-testid={`po-line-item-${i}`} aria-label="Item" className={`${cls} ${err && !l.item_id ? "border-rose-400" : ""}`}>
                    {items.map(it => <option key={it.id} value={it.id}>{it.sku} — {it.name}</option>)}
                  </select>
                  <NumberInput min="0" value={l.qty} onChange={e => update(i, { qty: e.target.value })}
                         data-testid={`po-line-qty-${i}`} placeholder="Qty" aria-label="Quantity" className={`${cls} ${err && !(Number(l.qty) > 0) ? "border-rose-400" : ""}`}/>
                  <NumberInput min="0" value={l.unit_cost} onChange={e => update(i, { unit_cost: e.target.value })}
                         data-testid={`po-line-cost-${i}`} placeholder="Unit ₹" aria-label="Unit cost (₹)" className={cls}/>
                  <button onClick={() => rm(i)} disabled={lines.length === 1}
                          className="p-2 rounded-lg hover:bg-rose-50 text-rose-600 disabled:opacity-30 disabled:hover:bg-transparent">
                    <Trash2 className="w-4 h-4"/>
                  </button>
                </div>
                {err && <p className="text-[11px] text-rose-500 mt-1" data-testid={`po-line-error-${i}`}>{err}</p>}
              </div>
            );})}
            <button onClick={add} className="text-[12px] text-hocp-navy font-medium hover:underline">+ Add line</button>
          </div>
        </div>
        <div className="flex justify-end pt-2 border-t border-slate-100">
          <div className="text-right">
            <div className="text-[11px] uppercase text-slate-500 tracking-wider">Estimated total</div>
            <div className="text-[22px] font-display font-semibold text-hocp-navy tabular-nums">{fmtINR(total)}</div>
          </div>
        </div>
      </div>
      <Footer onClose={onClose} onSubmit={submit} saving={saving} testid="po-submit"/>
    </Modal>
  );
}

function PODetailDialog({ po, items, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const itemMap = Object.fromEntries(items.map(i => [i.id, i]));

  const doCancel = async () => {
    if (!confirm(`Cancel PO ${po.code}?`)) return;
    setBusy(true);
    try { await supAPI.cancelPO(po.id); toast.success("Cancelled"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`Purchase Order ${po.code}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 text-[13px]">
          <Cell label="Vendor">{po.vendor_name}</Cell>
          <Cell label="Status"><span className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${STATUS_STYLES[po.status]}`}>{po.status.replace("_", " ")}</span></Cell>
          <Cell label="Total">{fmtINR(po.total_amount)}</Cell>
          <Cell label="Expected">{po.expected_date || "—"}</Cell>
        </div>
        <div>
          <div className="text-[11.5px] uppercase tracking-wider text-slate-500 font-medium mb-2">Lines</div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Item</th>
                  <th className="text-right px-3 py-2">Qty</th>
                  <th className="text-right px-3 py-2">Unit ₹</th>
                  <th className="text-right px-3 py-2">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {(po.items || []).map((li, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-3 py-2">{itemMap[li.item_id]?.name || li.item_id}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{li.qty} {itemMap[li.item_id]?.uom || ""}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtINR(li.unit_cost)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtINR(li.qty * li.unit_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {["sent", "partially_received"].includes(po.status) && (
        <div className="pt-4 mt-4 border-t border-slate-100 flex justify-end gap-2 -mx-5 -mb-5 px-5 py-3 bg-slate-50">
          <button onClick={doCancel} disabled={busy} data-testid="po-cancel"
                  className="h-9 px-3.5 rounded-lg text-rose-700 text-[13px] font-medium hover:bg-rose-50 inline-flex items-center gap-1.5">
            <Ban className="w-4 h-4"/> Cancel PO
          </button>
        </div>
      )}
    </Modal>
  );
}

function Cell({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</div>
      <div className="text-[13px] text-slate-800 font-medium">{children}</div>
    </div>
  );
}
